/**
 * HTMLMinimap - Standalone HTML Canvas minimap with fog-of-war
 * 
 * Features:
 * - Renders to HTML canvas (independent of Phaser)
 * - Shows only explored tiles (fog-of-war)
 * - Displays local player position
 * - Hides items, enemies, and other players
 * - Handles viewport resize gracefully
 * - Preserves maze aspect ratio (no stretching)
 */

import { TileType } from '../systems/MazeGenerator';
import { TILE_SIZE } from '../config/game.config';

export interface MinimapConfig {
    pixelScale?: number;     // Pixels per tile (default: 4) - each tile is this many pixels
    margin?: number;         // Margin from screen edge (default: 10)
    revealRadius?: number;   // Tiles revealed around player (default: 3)
}

export class HTMLMinimap {
    private container: HTMLDivElement;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;

    private mazeData: TileType[][];
    private explored: boolean[][];  // Fog-of-war tracking

    private playerTileX: number = 0;  // Player tile position (snapped)
    private playerTileY: number = 0;

    private pixelScale: number;       // Pixels per tile
    private revealRadius: number;

    // Colors
    private readonly COLOR_WALL = '#2d3436';
    private readonly COLOR_FLOOR = '#636e72';
    private readonly COLOR_SPAWN = '#00b894';
    private readonly COLOR_FOG = '#1a1a2e';
    private readonly COLOR_PLAYER = '#4ecdc4';
    private readonly COLOR_BORDER = '#ffffff';

    constructor(mazeData: TileType[][], config: MinimapConfig = {}) {
        // Pixel scale: how many screen pixels per maze tile
        this.pixelScale = config.pixelScale ?? 4;
        const margin = config.margin ?? 10;
        this.revealRadius = config.revealRadius ?? 3;

        this.mazeData = mazeData;

        // Calculate canvas size based on maze dimensions and pixel scale
        const mazeWidth = mazeData[0].length;
        const mazeHeight = mazeData.length;
        const width = mazeWidth * this.pixelScale;
        const height = mazeHeight * this.pixelScale;

        // Initialize fog-of-war (all unexplored)
        this.explored = mazeData.map(row => row.map(() => false));

        // Create container
        this.container = document.createElement('div');
        this.container.id = 'html-minimap';
        this.container.style.cssText = `
            position: fixed;
            top: ${margin}px;
            right: ${margin}px;
            width: ${width}px;
            height: ${height}px;
            border: 2px solid ${this.COLOR_BORDER};
            border-radius: 4px;
            z-index: 100;
            pointer-events: none;
            overflow: hidden;
            background: ${this.COLOR_FOG};
            image-rendering: pixelated;
        `;

        // Create canvas
        this.canvas = document.createElement('canvas');
        this.canvas.width = width;
        this.canvas.height = height;
        this.canvas.style.cssText = 'width: 100%; height: 100%; image-rendering: pixelated;';


        this.ctx = this.canvas.getContext('2d')!;
        this.container.appendChild(this.canvas);
        document.body.appendChild(this.container);

        // Initial render
        this.render();
    }


    /**
     * Update player position and reveal nearby tiles
     */
    updatePlayerPosition(worldX: number, worldY: number): void {
        // Convert world coordinates to tile coordinates (snapped to grid)
        const tileX = Math.floor(worldX / TILE_SIZE);
        const tileY = Math.floor(worldY / TILE_SIZE);

        // Only re-render if position changed
        if (tileX === this.playerTileX && tileY === this.playerTileY) return;

        this.playerTileX = tileX;
        this.playerTileY = tileY;

        // Reveal tiles around player
        this.revealArea(tileX, tileY, this.revealRadius);

        // Re-render
        this.render();
    }

    /**
     * Reveal tiles in a radius around a point
     */
    private revealArea(centerX: number, centerY: number, radius: number): void {
        for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
                const x = centerX + dx;
                const y = centerY + dy;

                // Check bounds
                if (y >= 0 && y < this.mazeData.length &&
                    x >= 0 && x < this.mazeData[0].length) {
                    // Circular reveal (within radius)
                    if (dx * dx + dy * dy <= radius * radius) {
                        this.explored[y][x] = true;
                    }
                }
            }
        }
    }

    /**
     * Render the minimap
     */
    private render(): void {
        const ctx = this.ctx;
        const width = this.canvas.width;
        const height = this.canvas.height;
        const ps = this.pixelScale;

        // Clear canvas
        ctx.fillStyle = this.COLOR_FOG;
        ctx.fillRect(0, 0, width, height);

        // Draw explored tiles (each tile is pixelScale x pixelScale pixels)
        for (let y = 0; y < this.mazeData.length; y++) {
            for (let x = 0; x < this.mazeData[y].length; x++) {
                if (!this.explored[y][x]) continue;

                const tile = this.mazeData[y][x];
                const drawX = x * ps;
                const drawY = y * ps;

                switch (tile) {
                    case TileType.WALL:
                        ctx.fillStyle = this.COLOR_WALL;
                        break;
                    case TileType.FLOOR:
                        ctx.fillStyle = this.COLOR_FLOOR;
                        break;
                    case TileType.SPAWN:
                        ctx.fillStyle = this.COLOR_SPAWN;
                        break;
                    default:
                        ctx.fillStyle = this.COLOR_FLOOR;
                }

                ctx.fillRect(drawX, drawY, ps, ps);
            }
        }

        // Draw player as a single pixel block (snapped to tile grid)
        ctx.fillStyle = this.COLOR_PLAYER;
        ctx.fillRect(this.playerTileX * ps, this.playerTileY * ps, ps, ps);
    }

    /**
     * Reset fog-of-war (e.g., on new round)
     */
    reset(): void {
        this.explored = this.mazeData.map(row => row.map(() => false));
        this.render();
    }

    /**
     * Update maze data (if maze changes)
     */
    setMazeData(mazeData: TileType[][]): void {
        this.mazeData = mazeData;
        const width = mazeData[0].length * this.pixelScale;
        const height = mazeData.length * this.pixelScale;

        this.canvas.width = width;
        this.canvas.height = height;
        this.container.style.width = `${width}px`;
        this.container.style.height = `${height}px`;

        this.explored = mazeData.map(row => row.map(() => false));
        this.playerTileX = 0;
        this.playerTileY = 0;
        this.render();
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.container.remove();
    }
}
